import type { LevelDefinition } from './Level.ts';

/**
 * Level 7 — two pushes.
 *
 * The second level with no attractor in its solution, and the first that needs two magnets
 * of the same polarity. One negative above the fluid is the only way to get it out of the
 * shaft, and it drops straight down onto the room floor. A second negative, low and to the
 * left, bends that fall sideways into a jump that clears the vault wall.
 *
 * One magnet cannot be both: the launcher has to be above the fluid to push it down and
 * out, and the kicker has to be below it to push it sideways on the way past. That is the
 * whole puzzle, and it is why a single magnet solves it from none of 1178 grid positions.
 *
 * The solution family is deliberately wide — 86 of 320 launcher/kicker pairs work — so
 * only the idea is gated, not the pixel.
 *
 * Proven headlessly: the intended pair solves in 6.3s; the launcher alone drops the fluid
 * on the room floor at (391, 1342); the kicker alone never gets it out of the shaft; and
 * launching and then trying to catch it with an attractor fails, because a stream pulled
 * by a magnet crosses the vault mouth without noticing it.
 */
export const level07: LevelDefinition = {
  id: 'level07',
  name: 'The Kick',
  hint: 'One magnet only drops it. A second, low and behind, turns the fall into a jump.',
  magnetsAvailable: 2,
  fluid: {
    amount: 380,
    source: { x: 215, y: 420 },
    radius: 44,
  },
  goal: { x: 790, y: 1270, radius: 150, type: 'fluid', fraction: 0.4 },
  walls: [
    // Arena.
    { type: 'segment', x1: 100, y1: 200, x2: 900, y2: 200, thickness: 26 },
    { type: 'segment', x1: 900, y1: 200, x2: 900, y2: 1420, thickness: 26 },
    { type: 'segment', x1: 900, y1: 1420, x2: 100, y2: 1420, thickness: 26 },
    { type: 'segment', x1: 100, y1: 1420, x2: 100, y2: 200, thickness: 26 },

    // The shaft the fluid starts in: its only exit is downward.
    { type: 'segment', x1: 330, y1: 200, x2: 330, y2: 640, thickness: 26 },

    // The vault, with a lip on top so a lazy trajectory clips it.
    { type: 'segment', x1: 640, y1: 900, x2: 640, y2: 1420, thickness: 26 },
    { type: 'segment', x1: 640, y1: 900, x2: 700, y2: 900, thickness: 26 },
  ],
};
